// Media lives in R2 (VITE_MEDIA_BASE). Raster images are resized on the fly by the
// Netlify Image CDN; video/gif pass through. See scripts/media-sync.ts.
import { MEDIA, type MediaPath } from '~/generated/media'

const BASE = (import.meta.env.VITE_MEDIA_BASE ?? '').replace(/\/$/, '')

// URL of the original in the store.
export const media = (path: MediaPath): string => `${BASE}/${encodeURI(path)}`

export interface ImgOpts {
	w?: number
	h?: number
	fit?: 'contain' | 'cover' | 'fill'
	fm?: 'avif' | 'webp' | 'jpg' | 'png'
	q?: number
}

// gif and video are never transformed.
const RASTER = /\.(jpe?g|png|webp|avif)$/i

// Netlify Image CDN transform. Not available under `vite dev`, so the raw URL is returned there.
export const img = (url: string, opts: ImgOpts = {}): string => {
	if (!RASTER.test(url.split('?')[0])) return url
	if (import.meta.env.DEV) return url

	const p = new URLSearchParams({ url })
	if (opts.w) p.set('w', String(opts.w))
	if (opts.h) p.set('h', String(opts.h))
	if (opts.fit) p.set('fit', opts.fit)
	p.set('fm', opts.fm ?? 'avif')
	p.set('q', String(opts.q ?? 72))
	return `/.netlify/images?${p.toString()}`
}

// Width-descriptor srcset. `aspect` (w/h) keeps cropped candidates on-ratio.
export const srcSet = (
	url: string,
	widths: number[],
	opts: ImgOpts = {},
	aspect?: number,
): string =>
	widths
		.map((w) => `${img(url, { ...opts, w, h: aspect ? Math.round(w / aspect) : opts.h })} ${w}w`)
		.join(', ')

export const TIERS = {
	thumb: { widths: [400, 600, 800, 1200], fit: 'cover' as const, q: 65 },
	detail: { widths: [1200, 1600, 2000, 2400], fit: 'contain' as const, q: 74 },
} satisfies Record<string, { widths: number[]; fit: ImgOpts['fit']; q: number }>

export type Tier = keyof typeof TIERS

// Single transformed URL for a manifest asset at a given width (posters, backgrounds).
export const sized = (path: MediaPath, w: number, opts: ImgOpts = {}): string => {
	const m = MEDIA[path]
	return img(media(path), { w, h: Math.round(w / (m.w / m.h)), fit: 'cover', q: 70, ...opts })
}
